"use client";

import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { setMailMessage, setMailRoomsLoad, setMailUnreadCount } from "../../redux/mailSlice";
import { getUser, setMailId } from "../../Localstorage/localstorage";
import SelectPatientMail from "../../Componenten/SelectPatientMail";
import { markUnreadMessagesRead, seenMessage } from "../../apiCalls/messageApi";
import { wa } from "./mailWaTheme";
import { avatarInitial, contactAvatarUrl } from "../../utils/mailContactAvatar";
import { getTotalUnreadCount, getUnreadBadgeLabel, getUnreadCount } from "./mailUnread";
import { useInfiniteMailRooms } from "./useInfiniteMailRooms";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const MailboxAdmin = ({ setOpenModal, onConversationOpen, mobileChatOpen, roomsRefreshToken }) => {
  const { mailRooms, mailMessage, mailUnreadCount } = useSelector((state) => state.mail);
  const dispatch = useDispatch();
  const user = getUser();
  const [isMarkingRead, setIsMarkingRead] = useState(false);
  const loadedUnreadCount = useMemo(
    () => getTotalUnreadCount(mailRooms),
    [mailRooms]
  );
  const totalUnreadCount = mailUnreadCount == null ? loadedUnreadCount : mailUnreadCount;
  const {
    hasMoreRooms,
    isLoadingRooms,
    resetRooms,
    roomsError,
    scrollRootRef,
    searchQuery,
    sentinelRef,
    setSearchQuery,
  } = useInfiniteMailRooms({
    dispatch,
    mailRooms,
    refreshToken: roomsRefreshToken,
    user,
  });

  const updateMailMessage = useCallback(async (p_items, isClicked = true) => {
    setMailId(p_items);
    if (isClicked === true) {
      const threadUnreadCount = getUnreadCount(p_items);
      // WhatsApp-like: hide unread badge immediately when opening thread.
      dispatch(
        setMailRoomsLoad(
          (mailRooms || []).map((room) =>
            room?.id === p_items?.id ? { ...room, sum_status: 0 } : room
          )
        )
      );
      dispatch(
        setMailUnreadCount(
          Math.max(0, Number(totalUnreadCount || 0) - threadUnreadCount)
        )
      );
      seenMessage(user, p_items, dispatch);
    }
    dispatch(setMailMessage(p_items));
    if (isClicked) onConversationOpen?.();
  }, [dispatch, mailRooms, onConversationOpen, totalUnreadCount, user]);

  useEffect(() => {
    if (!mailMessage?.id && mailRooms?.length > 0) updateMailMessage(mailRooms[0], false);
  }, [mailMessage?.id, mailRooms, updateMailMessage]);

  const handleMarkAllRead = useCallback(async () => {
    if (isMarkingRead || Number(totalUnreadCount || 0) <= 0) return;
    setIsMarkingRead(true);
    try {
      await markUnreadMessagesRead(user, dispatch);
      dispatch(
        setMailRoomsLoad(
          (mailRooms || []).map((room) => ({
            ...room,
            sum_status: 0,
            unread_count: 0,
            unread_messages_count: 0,
          }))
        )
      );
      dispatch(setMailUnreadCount(0));
      resetRooms();
    } finally {
      setIsMarkingRead(false);
    }
  }, [dispatch, isMarkingRead, mailRooms, resetRooms, totalUnreadCount, user]);

  const roomRows = useMemo(() => {
    if (!mailRooms?.length) return null;
    return mailRooms.map((message) => {
      const active = message.id === mailMessage?.id;
      const unreadCount = getUnreadCount(message);
      const unread = unreadCount > 0;
      const unreadLabel = getUnreadBadgeLabel(unreadCount);
      const label = message?.sender || "Onbekend";
      const initial = avatarInitial(label);
      const rowAvatar = contactAvatarUrl(message, user?.id);
      return (
        <li key={message?.id} className="px-2 py-1.5">
          <button
            type="button"
            onClick={() => updateMailMessage(message, true)}
            className={classNames(
              "flex w-full gap-3 rounded-2xl border px-3 py-3 text-left transition-all duration-150",
              active
                ? "border-wa-accent/45 bg-wa-accent/10 shadow-[0_8px_24px_rgba(0,168,132,0.14)]"
                : "border-transparent",
              !active && "hover:border-wa-border hover:bg-wa-hover"
            )}
          >
            <div
              className={classNames(
                "relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-full border border-wa-border/70 text-lg font-medium",
                wa.panel2,
                wa.text
              )}
            >
              {rowAvatar ? (
                <img src={rowAvatar} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
              ) : (
                <span className="flex h-full w-full items-center justify-center">{initial}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <p className={classNames("truncate font-medium", unread ? "font-semibold" : "", wa.text)}>
                    {label}
                  </p>
                  {unread ? (
                    <span
                      className={classNames(
                        "inline-flex h-5 min-w-[1.25rem] flex-shrink-0 items-center justify-center rounded-full px-1 text-xs font-semibold",
                        active ? "bg-wa-accentdeep text-white" : wa.badge
                      )}
                      aria-label={`${unreadCount} ongelezen berichten`}
                    >
                      {unreadLabel}
                    </span>
                  ) : null}
                </div>
                <time dateTime={message?.lastdate_updated} className={classNames("flex-shrink-0 text-xs", wa.muted)}>
                  {moment(message?.lastdate_updated).format("HH:mm")}
                </time>
              </div>
              <div className="mt-0.5 flex items-center justify-between gap-2">
                <p className={classNames("truncate text-sm", unread ? wa.text : wa.muted)}>
                  {message?.subject || "—"}
                </p>
              </div>
            </div>
          </button>
        </li>
      );
    });
  }, [mailMessage?.id, mailRooms, updateMailMessage, user?.id]);

  return (
    <aside
      className={classNames(
        "flex h-full min-h-0 w-full flex-shrink-0 flex-col border-r md:max-w-[420px] md:w-[min(100%,400px)]",
        wa.border,
        wa.panel,
        mobileChatOpen ? "hidden md:flex" : "flex"
      )}
    >
      <header
        className={classNames(
          "flex flex-shrink-0 items-center justify-between gap-2 px-3 py-2.5",
          wa.panel2
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <div
            className={classNames(
              "relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full",
              wa.badge,
              "text-sm font-semibold"
            )}
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5" aria-hidden />
            {totalUnreadCount > 0 ? (
              <span
                className="absolute -right-1 -top-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white ring-2 ring-wa-panel2"
                aria-label={`${totalUnreadCount} ongelezen berichten`}
              >
                {getUnreadBadgeLabel(totalUnreadCount)}
              </span>
            ) : null}
          </div>
          <div className="min-w-0">
            <h2 className={classNames("truncate text-lg font-semibold leading-tight", wa.text)}>
              Berichten
            </h2>
            <p className={classNames("text-xs", wa.muted)}>
              {mailRooms?.length || 0} gesprekken
            </p>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={resetRooms}
            title="Vernieuwen"
            className={classNames(
              "rounded-full p-2 transition-colors",
              wa.muted,
              wa.hover,
              "hover:text-wa-text"
            )}
          >
            <ArrowPathIcon className="h-5 w-5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={handleMarkAllRead}
            disabled={isMarkingRead || Number(totalUnreadCount || 0) <= 0}
            title="Alle ongelezen berichten gelezen maken"
            aria-label="Alle ongelezen berichten gelezen maken"
            className={classNames(
              "rounded-full p-2 transition-colors disabled:cursor-not-allowed disabled:opacity-40",
              wa.muted,
              wa.hover,
              "hover:text-wa-text"
            )}
          >
            <EyeIcon className="h-5 w-5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setOpenModal("insert")}
            title="Nieuw bericht"
            className={classNames(
              "rounded-full p-2 transition-colors",
              wa.muted,
              wa.hover,
              "hover:text-wa-text"
            )}
          >
            <PlusIcon className="h-6 w-6" aria-hidden />
          </button>
          <button
            type="button"
            className={classNames(
              "rounded-full p-2 transition-colors",
              wa.muted,
              wa.hover,
              "hover:text-wa-text"
            )}
            aria-label="Meer"
          >
            <EllipsisVerticalIcon className="h-5 w-5" aria-hidden />
          </button>
        </div>
      </header>

      <div className={classNames("flex-shrink-0 border-b px-2 pb-2 pt-1", wa.border, wa.panel)}>
        <SelectPatientMail
          onSearchChange={setSearchQuery}
          search={searchQuery}
          user={user}
          key="Mail"
        />
      </div>

      <nav
        ref={scrollRootRef}
        aria-label="Gesprekken"
        className={classNames("min-h-0 flex-1 overflow-y-auto py-1", wa.panel)}
      >
        <ul role="list">
          {roomRows ? (
            roomRows
          ) : isLoadingRooms ? (
            <li className={classNames("px-6 py-12 text-center text-sm", wa.muted)}>
              Gesprekken laden...
            </li>
          ) : (
            <li className={classNames("px-6 py-12 text-center text-sm", wa.muted)}>
              Geen gesprekken. Start een nieuw bericht of pas je zoekopdracht aan.
            </li>
          )}
          <li
            ref={sentinelRef}
            className={classNames("px-6 py-3 text-center text-xs", roomsError ? "text-red-400" : wa.muted)}
          >
            {roomsError || (roomRows && isLoadingRooms ? "Gesprekken laden..." : "")}
            {!roomsError && roomRows && !hasMoreRooms ? "Alle gesprekken geladen" : ""}
          </li>
        </ul>
      </nav>
    </aside>
  );
};

export default MailboxAdmin;
